
import React from "react";
import Ussd from "../methods/Ussd";
import Wallet from "../methods/Wallet";
import { i18nManager } from "../../i18n";
import GiftCard from "../methods/GiftCard";
import { Schemes, Utils } from "../../utils";
import { Svgs } from "../../constants/Images";
import { ITransaction } from "../../services";
import Card from "../methods/card/Card";
import MobileMoney from "../methods/MobileMoney";
import BankTransfer from "../methods/BankTransfer";
import { Classname, Dropdown, useSubscription } from "@rivtn/noseur";
import loader from "../../assets/images/loading_circle.gif";
import ExpiryTimeKeeper from "../../utils/ExpiryTimeKeeper";
import { PaymentMethod, PaymentMethodAttributes } from "../../constants/PaymentMethod";

export interface CompoundProps {
    language: string;
    businessId: string;
    transaction: ITransaction;
    switchTransactionCountry: (country: string) => void;
    fetchTransaction: (queryOnly?: boolean, onDone?: (result?: ITransaction, error?: any) => void) => void;
    paymentMethodState: [PaymentMethod | undefined, React.Dispatch<React.SetStateAction<PaymentMethod | undefined>>];
}

function Compound(props: CompoundProps) {

    const businessId = props.businessId;
    const transaction = props.transaction;
    const labels = i18nManager.Labels.compound;
    const loadingState = React.useState<boolean>(false);
    const expiryDate = new Date(transaction.expiry_date);
    const [paymentMethod, setPaymentMethod] = props.paymentMethodState;
    const [showDetails, setShowDetails] = React.useState<boolean>(false);
    const [mainPanelHeight, setMainPanelHeight] = React.useState<string>("auto");
    const [disablePaymentMethodItems, setDisablePaymentMethodItems] = React.useState<boolean>(false);
    const countryAndCurrency = i18nManager.CountriesAndCurrencies.find(l => l.value === transaction.charge_country_code);
    const [paymentFormStyle, setPaymentFormStyle] = React.useState<{ width: string; paddingBottom: string; }>({ width: "400px", paddingBottom: "60px" });
    useSubscription("MOBILE.COMPOUND.MAIN_PANEL.STYLE.HEIGHT", setMainPanelHeight);
    useSubscription("MOBILE.COMPOUND.PAYMENT_FORM.STYLE", setPaymentFormStyle);

    const componentPayload = {
        businessId,
        transaction,
        loadingState,
        paymentSuccessful,
        fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => props.fetchTransaction(true, onDone),
    };

    return (<div className="compound mobile">
        <div className="container mobile">
            {loadingState[0] && (<div className="container-loading">
                <img src={loader} alt={i18nManager.Labels.loading.loader_alt} />
            </div>)}
            <span className="title">{i18nManager.Labels.name}</span>
            <div className="txn-info">
                <div className="cou-curr">
                    <Dropdown borderless scheme={Schemes.RIVTN_PLOUTOUS} options={i18nManager.CountriesAndCurrencies} formControlProps={{ className: "country-dropdown" }}
                        selectedOptionIndex={i18nManager.CountriesAndCurrencies.findIndex(c => c.value === transaction?.charge_country_code)}
                        onSelectOption={(option) => {
                            props.switchTransactionCountry(option.value);
                            return true;
                        }} disabled={!transaction.configuration.can_change_country} />
                    <Dropdown borderless scheme={Schemes.RIVTN_PLOUTOUS} options={i18nManager.Languages}
                        selectedOptionIndex={i18nManager.Languages.findIndex(l => l.value === props.language)}
                        onSelectOption={(option) => {
                            i18nManager.switchLanguage(option.value);
                            return true;
                        }} disabled={!transaction.configuration.can_change_language} />
                </div>
                <div className="mail-amount">
                    <span className="email">{Utils.collapseString(transaction.payer_email, 25)}</span>
                    <span className="amount">{labels.details.pay} <span>{countryAndCurrency?.currency_sign} {(transaction.amount/ Utils.AMOUNT_UNIT)}</span></span>
                </div>
            </div>
            <div className="navigator mobile">
                <div className={Classname.build("methods", { "disable": disablePaymentMethodItems })}>
                    {transaction.payment_options.filter((paymentOption, index) => paymentOption.method in PaymentMethodAttributes).map((paymentOption) => {
                        const methodActive = paymentMethod === paymentOption.method;
                        const paymentMethodAttribute = PaymentMethodAttributes[paymentOption.method];

                        return (<React.Fragment key={paymentOption.method}>
                            <div key={`${paymentOption.method}-el`} onClick={disablePaymentMethodItems ? undefined : () => setPaymentMethod(paymentOption.method)}
                                className={Classname.build("method", { "active": methodActive, "noseur-disabled": disablePaymentMethodItems && !methodActive }, paymentOption.method.toLocaleLowerCase())}>
                                <paymentMethodAttribute.icon />
                            </div>
                        </React.Fragment>);
                    })}
                </div>
            </div>
            <div className="main-panel" style={{ height: mainPanelHeight }}>
                <div className="payment-form" style={paymentFormStyle}>
                    {paymentMethod === PaymentMethod.CARD && (<Card {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.USSD && (<Ussd {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.WALLET && (<Wallet {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.GIFT_CARD && (<GiftCard {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.MOBILE_MONEY && (<MobileMoney {...componentPayload} />)}
                    {paymentMethod === PaymentMethod.BANK_TRANSFER && (<BankTransfer {...componentPayload} />)}
                </div>
            </div>
            <div className="details-toggle" onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? Svgs.TriangleRight : Svgs.TriangleLeft}
            </div>
            {showDetails && (<div className="details mobile">
                <p className="title">{labels.details.title}</p>
                <div>
                    <div className="info">
                        <div className="line">
                            <span>{labels.details.service}</span>
                            <span>{transaction.service_name}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.tid}</span>
                            <span className="lined-limit" style={{ "--maxTextLine": 1 } as any}>{transaction.reference}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.description}</span>
                            <span className="lined-limit">{transaction.description}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.country}</span>
                            <span>{countryAndCurrency?.label}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.currency}</span>
                            <span>{countryAndCurrency?.currency_name}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.request_amount}</span>
                            <span>{countryAndCurrency?.currency_sign} {(transaction.charge_amount/ Utils.AMOUNT_UNIT)}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.tax}</span>
                            <span>{countryAndCurrency?.currency_sign} {(transaction.tax/ Utils.AMOUNT_UNIT)}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.discount}</span>
                            <span>{countryAndCurrency?.currency_sign} {(transaction.discount/ Utils.AMOUNT_UNIT)}</span>
                        </div>
                        <div className="line">
                            <span>{labels.details.amount_paid}</span>
                            <span>{countryAndCurrency?.currency_sign} {(transaction.amount_paid/ Utils.AMOUNT_UNIT)}</span>
                        </div>
                    </div>
                    <ExpiryTimeKeeper reportExpiry={() => props.fetchTransaction()} expiryDate={expiryDate} />
                    <div className="line">
                        <span>{labels.details.total_amount}</span>
                        <span>{countryAndCurrency?.currency_sign} {(transaction.amount/ Utils.AMOUNT_UNIT)}</span>
                    </div>
                </div>
            </div>)}
        </div>
    </div>);

    function paymentSuccessful() {
        setDisablePaymentMethodItems(true);
    }

}

export default Compound;
